import { DTOBlock } from '@/api/generated-catch-backend/model';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PortableText } from '../../../portable-text';

type InterstitialLoaderProps = {
  interstitial: {
    title: string;
    subtitleBelow?: DTOBlock[] | null;
  };
  onNext: () => void;
};

const DURATION_MS = 2600;

export const InterstitialLoader = ({ interstitial, onNext }: InterstitialLoaderProps) => {
  const [progress, setProgress] = useState(0);
  const hasAdvanced = useRef(false);

  useEffect(() => {
    const start = performance.now();
    let raf: number;

    const tick = (now: number) => {
      const elapsed = now - start;
      const pct = Math.min(elapsed / DURATION_MS, 1);
      setProgress(pct);

      if (pct < 1) {
        raf = requestAnimationFrame(tick);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const handleComplete = useCallback(() => {
    if (!hasAdvanced.current) {
      hasAdvanced.current = true;
      onNext();
    }
  }, [onNext]);

  useEffect(() => {
    if (progress >= 1) {
      const timeout = setTimeout(handleComplete, 400);
      return () => clearTimeout(timeout);
    }
  }, [progress, handleComplete]);

  const percentage = Math.round(progress * 100);

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gray-50 px-6">
      {/* Spinning ring */}
      <div className="mb-8 h-16 w-16 animate-spin rounded-full border-4 border-gray-200 border-t-teal-400" />

      {/* Title */}
      <h1 className="mb-4 max-w-md text-center text-xl font-bold text-gray-900">{interstitial.title}</h1>

      {/* Subtitle */}
      {interstitial.subtitleBelow && interstitial.subtitleBelow.length > 0 && (
        <div className="mb-8 max-w-md text-center text-base text-gray-600">
          <PortableText content={interstitial.subtitleBelow} />
        </div>
      )}

      {/* Progress bar */}
      <div className="w-full max-w-xs">
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
          <div
            className="h-full rounded-full bg-teal-400 transition-none"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <p className="mt-2 text-center text-sm text-gray-500">{percentage}%</p>
      </div>
    </div>
  );
};
